"use client"

import { motion } from "motion/react"
import { useEffect, useState, useMemo } from "react"

interface TocItem {
    id: string
    text: string
    level: number
}

interface TableOfContentsProps {
    content: string | any[] // Accept Sanity block content array
}

export function TableOfContents({ content }: TableOfContentsProps) {
    const [activeId, setActiveId] = useState<string>("")

    // Parse HTML or Portable Text content to find H2 and H3 headings
    const headings = useMemo<TocItem[]>(() => {
        if (!content) return []
        
        // Handle Sanity block content
        if (Array.isArray(content)) {
            return content
                .filter(block => block._type === 'block' && (block.style === 'h2' || block.style === 'h3'))
                .map((block, index) => {
                    const text = block.children?.map((c: any) => c.text).join('') || ""
                    const slug = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)+/g, "")
                    return {
                        id: slug || `section-${index}`,
                        text,
                        level: block.style === 'h2' ? 2 : 3
                    }
                })
        }
        
        // Handle raw HTML string
        if (typeof window === 'undefined') return [] // SSR guard for DOMParser
        const parser = new DOMParser()
        const doc = parser.parseFromString(content, "text/html")
        const elements = doc.querySelectorAll("h2, h3")

        return Array.from(elements).map((el, index) => {
            const _id = el.id || `section-${index}`
            const text = el.textContent || ""
            const slug = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)+/g, "")

            return {
                id: slug,
                text,
                level: Number(el.tagName.charAt(1))
            }
        })
    }, [content])

    // Scroll spy interaction
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveId(entry.target.id)
                    }
                })
            },
            { rootMargin: "-20% 0px -35% 0px" }
        )

        headings.forEach((heading) => {
            const element = document.getElementById(heading.id)
            if (element) observer.observe(element)
        })

        return () => observer.disconnect()
    }, [headings])

    const scrollToHeading = (id: string, e: React.MouseEvent) => {
        e.preventDefault()
        const element = document.getElementById(id)
        if (element) {
            window.scrollTo({
                top: element.offsetTop - 100,
                behavior: "smooth"
            })
            setActiveId(id)
        }
    }

    if (headings.length === 0) return null

    return (
        <nav className="hidden lg:block sticky top-32 max-h-[calc(100vh-8rem)] overflow-y-auto w-64 pl-4 border-l border-white/5">
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">
                On This Page
            </h4>
            <ul className="space-y-1">
                {headings.map((heading) => (
                    <li
                        key={heading.id}
                        style={{ paddingLeft: heading.level === 3 ? "1rem" : "0" }}
                    >
                        <a
                            href={`#${heading.id}`}
                            onClick={(e) => scrollToHeading(heading.id, e)}
                            className={`group flex items-center py-1.5 text-sm transition-colors duration-200 ${activeId === heading.id
                                ? "text-premium-cyan font-medium"
                                : "text-slate-500 hover:text-slate-300"
                                }`}
                        >
                            {activeId === heading.id && (
                                <motion.div
                                    layoutId="toc-indicator"
                                    className="absolute -left-[17px] w-0.5 h-4 bg-premium-cyan rounded-full"
                                />
                            )}
                            <span className="truncate">{heading.text}</span>
                        </a>
                    </li>
                ))}
            </ul>
        </nav>
    )
}
